import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Notification as NotificationTagDs, NotificationHeader } from '@adeccoux/tag-ds';
import DataCheckerDetails from '@features/oneOnboarding/pages/DataCheckerUpload/DataCheckerDetails';

import styles from './IDVerifyBanner.module.scss';

type IDVerifyBannerProps = {
  status: string;
  idVerifyData?: any;
};

const StatusConfig = {
  APPROVED: { icon: 'check_circle', iconColor: '#73D85A' },
  DISAPPROVED: { icon: 'warning', iconColor: '#E87A71' },
  TECHNICAL_REJECT: { icon: 'warning', iconColor: '#FFC133' },
  DEFAULT: { icon: 'info', iconColor: '#999999' },
} as const;

const Status = {
  APPROVED: 'APPROVED',
  DISAPPROVED: 'DISAPPROVED',
  TECHNICAL_REJECT: 'TECHNICAL_REJECT',
};

type StatusKey = keyof typeof StatusConfig;
type StatusInfo = typeof StatusConfig[StatusKey];

const toStatusKey = (value: string): StatusKey => (value in StatusConfig ? (value as StatusKey) : 'DEFAULT');

export const IDVerifyBanner: React.FC<IDVerifyBannerProps> = ({ status, idVerifyData }) => {
  const { t } = useTranslation(['documents']);
  const [isActiveDcDetails, setisActiveDcDetails] = useState<boolean>(false);

  const info: StatusInfo = StatusConfig[toStatusKey(status)];
  const hasDetails = status !== Status.APPROVED;

  const detailControls = {
    isActiveDcDetails,
    selectedDcResults: idVerifyData,
    onClose: () => setisActiveDcDetails(false),
  };

  return (
    <>
      <NotificationTagDs
        icon={
          <span className={`material-icons ${styles.icon}`} style={{ color: info.iconColor }}>
            {info.icon}
          </span>
        }
        show={true}
        success={status === Status.APPROVED}
        warning={status === Status.TECHNICAL_REJECT}
        error={status === Status.DISAPPROVED}
        className={`tag-ds ${styles.notification}`}
      >
        <div className={styles.row}>
          <NotificationHeader className={styles.header}>
            <span className={styles.headerLabel}>{t('documents:DATACHECKER.IdVerifyResultStatus.label')}:&nbsp;</span>
            <span className={styles.headerValue}>{t(`documents:DATACHECKER.IdVerifyResultStatus.${status}`)}</span>
          </NotificationHeader>

          {hasDetails && (
            <div className={styles.details}>
              <span className={styles.link} role="button" tabIndex={0} onClick={() => setisActiveDcDetails(true)}>
                {t('documents:DATACHECKER.link.label')}
              </span>
            </div>
          )}
        </div>
      </NotificationTagDs>

      <DataCheckerDetails detailControls={detailControls} />
    </>
  );
};
